import * as EppoSdk from '@eppo/js-client-sdk';
import Cookies from 'js-cookie';
import { createContext, useContext, useMemo } from 'react';

import { SEGMENT_ANONYMOUS_ID_COOKIE_NAME } from '~/common/constants/user';
import { useDeviceType } from '~/hooks/useDeviceType';

import { AuthenticationContext } from '../AuthenticationProvider/AuthenticationProvider';

const eppoClient = EppoSdk.getInstance();
const defaultValue = 'off';
const IS_LHCI_TESTING = process.env.LHCI_TESTING;

type Context = {
  isError: boolean;
  isInitialized: boolean;
};

export const EppoContext = createContext<Context>({
  isInitialized: false,
  isError: false,
});

export const useEppo = (): Context => useContext(EppoContext);

export const useEppoStringAssignment = (featureKey: string): string => {
  const { isInitialized } = useEppo();

  /**
   * Cannot use `useAuthenticationContext` here
   * because it will cause a circular dependency
   * between the EppoProvider and AuthenticationProvider.
   * So we need to use the context directly
   */
  const { user } = useContext(AuthenticationContext);
  const deviceType = useDeviceType();

  const subjectKey = Cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE_NAME) as string;

  const subjectAttributes = useMemo(
    () => ({
      city: user?.city || '',
      isSitter: user?.isSitter !== undefined ? user.isSitter : 'unset',
      state: user?.state || '',
      postCode: user?.postcode || '',
      userId: user?.id || '',
      deviceType,
    }),
    [user, deviceType]
  );

  /**
   * Get the assignment for the given feature key
   * If the eppo client is not initialized, return the default value
   */
  const assignment = useMemo(() => {
    /**
     * Return default if not initialised or the LHCI_TESTING flag is set
     * This is to ensure that the tests run without any performance issues
     */
    if (!isInitialized || IS_LHCI_TESTING) {
      return defaultValue;
    }

    return eppoClient.getStringAssignment(featureKey, subjectKey, subjectAttributes, defaultValue);
  }, [featureKey, subjectKey, subjectAttributes, isInitialized]);

  return assignment;
};
